/**
 * Video Preview Player Component (canvas-v2)
 *
 * Displays the current frame at the playhead position.
 * Handles client-side video sequencing across multiple clips.
 *
 * Architecture: premounts one <video> element per clip for zero-gap transitions.
 * - Each element uses a blob URL from the preloader (no network fetch at transition time)
 * - Eager pre-seek to trimStart on blob load and trim change
 * - Play-before-pause ordering at transitions: start new element BEFORE pausing old
 * - Only active clip's element is visible; all others are hidden
 */

import { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import type { TimelineClip } from './types'
import type { UseClipPreloaderReturn } from './useClipPreloader'
import { getAssetUrl, createNadDebug } from '@ponpon/api-client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import createDebug from 'debug'
const debug = createDebug('dw:timeline-playback:clip-transition')
const fadeLog = createNadDebug('dw:video-editor-improvements')

interface VideoPreviewPlayerProps {
  /** Current clips on timeline */
  clips: TimelineClip[]

  /** Current playhead position (seconds) */
  playheadPosition: number

  /** Is video currently playing */
  isPlaying: boolean

  /** Callback when video ends */
  onVideoEnd?: () => void

  /** Preloader providing blob URLs for all clips */
  preloader: UseClipPreloaderReturn

  /**
   * When true, every <video> is forced to `muted=true` regardless of
   * `clip.muted`. The canvas preview uses this for its global mute toggle so
   * the modal-owned per-clip mute is not mutated.
   */
  globalMuted?: boolean

  /** Hide the built-in clip-name overlay (the host renders its own). */
  hideClipBadge?: boolean

  /**
   * RFC-0651 #11: indices of video tracks that should be silenced. When
   * the active clip's trackIndex is in this set, its embedded audio is
   * muted (the <video> element keeps playing, only audio is suppressed).
   */
  mutedVideoTracks?: Set<number>
}

interface ClipWithTiming extends TimelineClip {
  /** Start time in final timeline (seconds) */
  timelineStart: number

  /** End time in final timeline (seconds) */
  timelineEnd: number
}

interface PreviewVideoElementProps {
  clip: ClipWithTiming
  isActive: boolean
  blobUrl: string | null
  setVideoRef: (clipId: string) => (el: HTMLVideoElement | null) => void
  onLoadedData: (clip: ClipWithTiming) => void
  onError: (clip: ClipWithTiming, hasSource: boolean) => void
  globalMuted?: boolean
  /** RFC-0651: composed track-mute + per-clip mute. */
  effectivelyMuted?: boolean
}

function PreviewVideoElement({
  clip,
  isActive,
  blobUrl,
  setVideoRef,
  onLoadedData,
  onError,
  globalMuted = false,
  effectivelyMuted = false,
}: PreviewVideoElementProps) {
  const projectId = useProjectId()
  const baseUrl = useMemo(() => {
    if (!projectId || !clip.sourcePath) return null
    return getAssetUrl(projectId, clip.sourcePath)
  }, [projectId, clip.sourcePath])
  const directUrl = useAuthenticatedMediaUrl(baseUrl, clip.sourcePath)
  // Source preference: blob URL (preloaded, instant playback) when
  // available, otherwise the auth'd direct URL. We DO NOT cache the
  // first sourceUrl in a stable ref — when the preloader re-mounts
  // (e.g. parent re-key, sourcePaths change, hot reload), it revokes
  // old blob URLs and creates fresh ones with new UUIDs. A stable-
  // ref cache would pin the dead URL forever and the <video> would
  // ERR_FILE_NOT_FOUND. The <video key={clip.id}> below stays
  // identity-stable across re-renders, so a src swap just triggers
  // a single reload — no element remount, no flash.
  const sourceUrl = blobUrl ?? directUrl ?? undefined

  // Local ref so we can imperatively sync volume (HTMLMediaElement.volume is
  // not a React attribute — it must be set on the DOM element directly).
  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const setRef = useCallback(
    (el: HTMLVideoElement | null) => {
      localVideoRef.current = el
      setVideoRef(clip.id)(el)
    },
    [clip.id, setVideoRef],
  )

  // Sync clip.volume to the underlying video element. clip.muted is set
  // declaratively via the React `muted` prop below.
  useEffect(() => {
    const el = localVideoRef.current
    if (!el) return
    const target = Math.max(0, Math.min(1, clip.volume))
    if (Math.abs(el.volume - target) > 0.001) {
      el.volume = target
    }
    debug(
      'clip-audio-applied: clipId=%s volume=%f muted=%s elVolume=%f elMuted=%s',
      clip.id,
      clip.volume,
      clip.muted,
      el.volume,
      el.muted,
    )
  }, [clip.id, clip.volume, clip.muted])

  return (
    <video
      key={clip.id}
      ref={setRef}
      src={sourceUrl}
      className="absolute inset-0 w-full h-full object-contain"
      style={{
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        zIndex: isActive ? 1 : 0,
      }}
      onLoadedData={() => {
        // Apply current volume on first load — `volume` is not a React DOM attr.
        const el = localVideoRef.current
        if (el) el.volume = Math.max(0, Math.min(1, clip.volume))
        onLoadedData(clip)
      }}
      onError={() => onError(clip, Boolean(sourceUrl))}
      playsInline
      preload="auto"
      muted={globalMuted || effectivelyMuted}
    />
  )
}

export function VideoPreviewPlayer({
  clips,
  playheadPosition,
  isPlaying,
  onVideoEnd,
  preloader,
  globalMuted = false,
  hideClipBadge = false,
  mutedVideoTracks,
}: VideoPreviewPlayerProps) {
  const videoRefsMap = useRef<Record<string, HTMLVideoElement | null>>({})
  const [activeClipId, setActiveClipId] = useState<string | null>(null)
  const activeClipIdRef = useRef<string | null>(null)
  const previousPlayheadRef = useRef(playheadPosition)

  // Track pre-seek state so we don't re-seek unnecessarily
  const preSeekMapRef = useRef<Map<string, number>>(new Map())

  // Compute clip timings (start/end times in the final timeline)
  const clipsWithTiming: ClipWithTiming[] = useMemo(() => {
    return [...clips]
      .sort((a, b) => a.trackIndex - b.trackIndex || a.startOffset - b.startOffset || a.order - b.order)
      .map((clip) => ({
        ...clip,
        timelineStart: clip.startOffset,
        timelineEnd: clip.startOffset + clip.effectiveDuration,
      }))
  }, [clips])

  // Find which clip should be shown at current playhead position
  const activeClip = useMemo(() => {
    return clipsWithTiming.find(
      (clip) => playheadPosition >= clip.timelineStart && playheadPosition < clip.timelineEnd
    )
  }, [clipsWithTiming, playheadPosition])

  // Fade overlay opacity (Issue 4 — video transition preview).
  // Black-to-clip ramp for fade-in (1 → 0 across first fadeInS seconds of clip).
  // Clip-to-black ramp for fade-out (0 → 1 across last fadeOutS seconds of clip).
  const fadeOpacity = useMemo(() => {
    if (!activeClip) return 0
    const t = playheadPosition - activeClip.timelineStart
    const eff = activeClip.effectiveDuration
    const fadeIn = Math.max(0, Math.min(eff, activeClip.fadeInS ?? 0))
    const fadeOut = Math.max(0, Math.min(eff, activeClip.fadeOutS ?? 0))
    const inOpacity = fadeIn > 0 && t < fadeIn ? 1 - t / fadeIn : 0
    const outOpacity =
      fadeOut > 0 && t > eff - fadeOut ? (t - (eff - fadeOut)) / fadeOut : 0
    return Math.max(0, Math.min(1, Math.max(inOpacity, outOpacity)))
  }, [activeClip, playheadPosition])

  // Throttle fade-overlay logging to avoid spamming nad-debug.log during scrub/play.
  const lastFadeLogRef = useRef(0)
  useEffect(() => {
    if (!activeClip) return
    const fadeIn = activeClip.fadeInS ?? 0
    const fadeOut = activeClip.fadeOutS ?? 0
    if (fadeIn === 0 && fadeOut === 0) return
    const now = Date.now()
    if (now - lastFadeLogRef.current < 250) return
    lastFadeLogRef.current = now
    fadeLog({
      event: 'fade-overlay-render',
      activeClipId: activeClip.id,
      t: Number((playheadPosition - activeClip.timelineStart).toFixed(3)),
      fadeOpacity: Number(fadeOpacity.toFixed(3)),
      fadeInS: fadeIn,
      fadeOutS: fadeOut,
    })
  }, [activeClip, playheadPosition, fadeOpacity])

  // Current clip index for display
  const currentClipIndex = activeClip
    ? clipsWithTiming.findIndex((c) => c.id === activeClip.id)
    : null

  // RFC-0651 #11: compose per-clip mute with per-track mute. When the
  // active clip's trackIndex is in `mutedVideoTracks`, the embedded audio
  // is silenced. We forward this as an OR with the existing per-clip flag
  // through a clipId→effective-mute map so PreviewVideoElement can render
  // declaratively.
  const effectiveMutedByClipId = useMemo(() => {
    const out = new Map<string, boolean>()
    for (const clip of clipsWithTiming) {
      const trackMuted = mutedVideoTracks?.has(clip.trackIndex) ?? false
      out.set(clip.id, clip.muted || trackMuted)
    }
    return out
  }, [clipsWithTiming, mutedVideoTracks])

  // Ref callback for collecting video element refs
  const setVideoRef = useCallback((clipId: string) => (el: HTMLVideoElement | null) => {
    videoRefsMap.current[clipId] = el
  }, [])

  // Eager pre-seek: when a video element loads or trim changes, seek to trimStart
  const handleLoadedData = useCallback(
    (clip: ClipWithTiming) => {
      const video = videoRefsMap.current[clip.id]
      if (!video) return

      if (clip.id === activeClipIdRef.current) {
        const relativeTime = Math.max(0, playheadPosition - clip.timelineStart)
        const videoTime = clip.trimStart + relativeTime
        if (Math.abs(video.currentTime - videoTime) > 0.05) {
          video.currentTime = videoTime
        }
        video.playbackRate = clip.speed
        if (isPlaying) {
          video.play().catch((err) => {
            if (err.name !== 'AbortError') {
              console.error('Video play after load failed:', err)
            }
          })
        }
      } else {
        debug('pre-seek on load: clip=%s trimStart=%.3f', clip.id, clip.trimStart)
        video.currentTime = clip.trimStart
        preSeekMapRef.current.set(clip.id, clip.trimStart)
      }
    },
    [isPlaying, playheadPosition]
  )

  // Re-seek when trimStart changes for non-active clips
  useEffect(() => {
    const preSeekMap = preSeekMapRef.current

    for (const clip of clipsWithTiming) {
      if (clip.id === activeClipIdRef.current) continue
      const video = videoRefsMap.current[clip.id]
      if (!video || video.readyState < 1) continue

      const lastTrimStart = preSeekMap.get(clip.id)
      if (lastTrimStart !== clip.trimStart) {
        debug('re-seek on trim change: clip=%s trimStart=%.3f', clip.id, clip.trimStart)
        video.currentTime = clip.trimStart
        preSeekMap.set(clip.id, clip.trimStart)
      }
    }
  }, [clipsWithTiming])

  // Handle clip transitions and playback sync
  useEffect(() => {
    if (!activeClip) {
      // Pause the previously-active element so its audio doesn't leak while
      // the playhead is sitting in a gap or past the last clip. Pre-RFC-0566
      // this was harmless because all preview elements were hardcoded muted;
      // now that we honor `clip.muted`, an unmuted clip would keep playing
      // audio even though it's hidden.
      const prevActiveId = activeClipIdRef.current
      if (prevActiveId) {
        const prevVideo = videoRefsMap.current[prevActiveId]
        if (prevVideo) {
          prevVideo.pause()
          const prevClip = clipsWithTiming.find((c) => c.id === prevActiveId)
          if (prevClip) {
            prevVideo.currentTime = prevClip.trimStart
            preSeekMapRef.current.set(prevActiveId, prevClip.trimStart)
          }
        }
      }
      activeClipIdRef.current = null
      setActiveClipId(null)
      return
    }

    const prevActiveId = activeClipIdRef.current
    const video = videoRefsMap.current[activeClip.id]

    if (prevActiveId !== activeClip.id) {
      // Clip transition
      debug(
        'clip transition: %s → %s (playing=%s)',
        prevActiveId,
        activeClip.id,
        isPlaying
      )

      if (video) {
        video.playbackRate = activeClip.speed
        const relativeTime = playheadPosition - activeClip.timelineStart
        const videoTime = activeClip.trimStart + relativeTime
        if (Math.abs(video.currentTime - videoTime) > 0.05) {
          video.currentTime = videoTime
        }

        if (isPlaying) {
          // Zero-gap play-before-pause ordering:
          // 1. Start new element FIRST
          video.play().catch((err) => {
            if (err.name !== 'AbortError') {
              console.error('Video play after transition failed:', err)
            }
          })

          // 2. Pause old element
          if (prevActiveId) {
            const prevVideo = videoRefsMap.current[prevActiveId]
            if (prevVideo) {
              prevVideo.pause()
              // Re-seek old element to its trimStart for reuse
              const prevClip = clipsWithTiming.find((c) => c.id === prevActiveId)
              if (prevClip) {
                prevVideo.currentTime = prevClip.trimStart
                preSeekMapRef.current.set(prevActiveId, prevClip.trimStart)
              }
            }
          }
        } else {
          // Not playing — just seek to correct position.
          video.currentTime = videoTime
        }
      }

      activeClipIdRef.current = activeClip.id
      setActiveClipId(activeClip.id)
    } else {
      const playheadJump = Math.abs(playheadPosition - previousPlayheadRef.current)
      const shouldSeek = !isPlaying || playheadJump > 0.25

      // Same clip, scrubbing or seeking while playing — seek to match playhead.
      if (video) {
        const relativeTime = playheadPosition - activeClip.timelineStart
        const videoTime = activeClip.trimStart + relativeTime
        if (shouldSeek && Math.abs(video.currentTime - videoTime) > 0.05) {
          video.currentTime = videoTime
          if (isPlaying && video.paused) {
            video.play().catch((err) => {
              if (err.name !== 'AbortError') {
                console.error('Video play after seek failed:', err)
              }
            })
          }
        }
      }
    }

    previousPlayheadRef.current = playheadPosition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playheadPosition, activeClip?.id, isPlaying])

  // Handle play/pause for current active video
  useEffect(() => {
    if (!activeClipId) return
    const video = videoRefsMap.current[activeClipId]
    if (!video) return

    const activeClipData = clipsWithTiming.find((c) => c.id === activeClipId)
    if (!activeClipData) return

    video.playbackRate = activeClipData.speed

    if (isPlaying) {
      video.play().catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Video play failed:', err)
        }
      })
    } else {
      video.pause()
    }
  }, [isPlaying, activeClipId, clipsWithTiming])

  // Monitor video time with RAF for frame-accurate clip boundary detection
  const boundaryRafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isPlaying || !activeClipId) {
      if (boundaryRafRef.current !== null) {
        cancelAnimationFrame(boundaryRafRef.current)
        boundaryRafRef.current = null
      }
      return
    }

    const activeClipData = clipsWithTiming.find((c) => c.id === activeClipId)
    if (!activeClipData) return

    const clipEnd = activeClipData.trimStart + activeClipData.effectiveDuration

    const checkBoundary = () => {
      const video = videoRefsMap.current[activeClipId]
      if (!video) return

      if (video.currentTime >= clipEnd) {
        video.pause()
        video.currentTime = activeClipData.trimStart
        preSeekMapRef.current.set(activeClipId, activeClipData.trimStart)
        return
      }

      boundaryRafRef.current = requestAnimationFrame(checkBoundary)
    }

    boundaryRafRef.current = requestAnimationFrame(checkBoundary)

    return () => {
      if (boundaryRafRef.current !== null) {
        cancelAnimationFrame(boundaryRafRef.current)
        boundaryRafRef.current = null
      }
    }
  }, [isPlaying, activeClipId, clipsWithTiming, onVideoEnd])

  const handleVideoError = useCallback((clip: ClipWithTiming, hasSource: boolean) => {
    if (hasSource) {
      console.error('Video load error for clip:', clip.label)
    }
  }, [])

  return (
    <div className="relative w-full h-full bg-black rounded-lg overflow-hidden border border-border">
      {clips.length > 0 ? (
        <>
          {/* Premount one <video> per clip */}
          <div className="relative w-full h-full">
            {clipsWithTiming.map((clip) => {
              const blobUrl = preloader.getBlobUrl(clip.sourcePath)
              const isActive = clip.id === activeClipId

              return (
                <PreviewVideoElement
                  key={clip.id}
                  clip={clip}
                  isActive={isActive}
                  blobUrl={blobUrl}
                  setVideoRef={setVideoRef}
                  onLoadedData={handleLoadedData}
                  onError={handleVideoError}
                  globalMuted={globalMuted}
                  effectivelyMuted={effectiveMutedByClipId.get(clip.id) ?? clip.muted}
                />
              )
            })}

            {/* Fade-to-black overlay (Issue 4). Sits above the active <video>
                element (z=1) but below the clip badge (z=10). Opacity is
                derived per render from playhead vs active clip's fade region. */}
            <div
              className="absolute inset-0 pointer-events-none bg-black"
              style={{ opacity: fadeOpacity, zIndex: 5 }}
              data-testid="timeline-preview-fade-overlay"
              data-fade-opacity={fadeOpacity.toFixed(3)}
              aria-hidden="true"
            />
          </div>

          {/* Preloader progress indicator */}
          {!preloader.isReady && preloader.loadingCount > 0 && (
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg border border-white/10 z-10">
              <p className="text-text-muted text-xxs">
                Preloading: {preloader.loadingCount} remaining
              </p>
            </div>
          )}

          {/* Clip info overlay */}
          {!hideClipBadge && activeClip && (
            <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg border border-white/10 z-10">
              <p className="text-foreground text-xs font-medium">{activeClip.label}</p>
              <p className="text-text-muted text-xxs">
                Clip {(currentClipIndex ?? 0) + 1} of {clips.length}
                {activeClip.speed !== 1.0 && ` | ${activeClip.speed}x`}
              </p>
            </div>
          )}
        </>
      ) : (
        /* No clips */
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-16 h-16 text-foreground/20 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-foreground/30 text-sm">No clips on timeline</p>
          </div>
        </div>
      )}
    </div>
  )
}
